import type { AfterViewInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Output,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { User } from '@core/account-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { OfferSimpleListItem } from '@core/offers-types';
import type { RadioGroupItem } from '@core/user-interface-legacy';
import {
  AvatarComponent,
  BoxComponent,
  DividerComponent,
  HeadingComponent,
  IconComponent,
  RadioFormGroupComponent,
  RadioFormGroupItemComponent,
  RtTooltip,
  TruncateComponent,
} from '@core/user-interface-legacy';

import type { AccessOptionsConfig, ShareOnTheGoI18n } from '../types';
import { AccessValue, SHARE_ON_THE_GO_I18N } from '../types';

@Component({
  selector: 'rt-user-item',
  templateUrl: './user-item.component.html',
  styleUrls: ['./user-item.component.less'],
  imports: [
    AvatarComponent,
    BoxComponent,
    DividerComponent,
    HeadingComponent,
    RadioFormGroupComponent,
    TruncateComponent,
    ReactiveFormsModule,
    RadioFormGroupItemComponent,
    IconComponent,
    TranslatePipe,
    RtTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserItemComponent implements AfterViewInit {
  private readonly i18n = inject(I18nService);
  private readonly cdRef = inject(ChangeDetectorRef);
  public readonly shareOnTheGoI18n = inject<ShareOnTheGoI18n>(SHARE_ON_THE_GO_I18N);

  public readonly user = input<User>(undefined!);

  public readonly accessOptionsConfig = input<AccessOptionsConfig>();

  public readonly offer = input<OfferSimpleListItem>();

  public selectedOption = new FormControl(AccessValue.CandidateOnly);
  public options: Array<RadioGroupItem<AccessValue>> = [];

  @Output()
  public valueChanges = this.selectedOption.valueChanges;

  @ViewChild('radioTpl', { static: true })
  private radioTpl: RadioGroupItem['template'];

  @ViewChild('radioWithInfoIconTpl', { static: true })
  private radioWithInfoIconTpl: RadioGroupItem['template'];

  public ngAfterViewInit(): void {
    this.setOptions();
    this.setFirstAvailableSelectedOption();

    this.cdRef.detectChanges();
  }

  public isOptionEnabled(optionItem: RadioGroupItem<AccessValue>): boolean {
    return this.accessOptionsConfig()![optionItem.value].isEnabled;
  }

  private setOptions(): void {
    if (!this.accessOptionsConfig()) {
      return;
    }

    this.options = <Array<RadioGroupItem<AccessValue>>>[
      {
        option: this.i18n.translate(
          'app.candidate.share_on_the_go.allow_access_to_candidate.allow_only_to_this_candidate',
        ),
        value: AccessValue.CandidateOnly,
        template: this.radioTpl,
      },
      {
        option: this.i18n.translate(
          'app.candidate.share_on_the_go.allow_access_to_candidate.allow_to_all_candidates_in_offer',
          { offerTitle: this.offer()!.title },
        ),
        value: AccessValue.AllCandidatesInOffer,
        template: this.radioTpl,
      },
      {
        option: this.i18n.translate(this.shareOnTheGoI18n.dontRequestOption, {
          fullUserName: `${this.user().firstName} ${this.user().lastName}`,
        }),
        value: AccessValue.DontRequest,
        template: this.radioWithInfoIconTpl,
      },
    ].filter(({ value }) => this.accessOptionsConfig()![value].isVisible);
  }

  private setFirstAvailableSelectedOption(): void {
    const accessOptionsConfig = this.accessOptionsConfig();
    if (!accessOptionsConfig) {
      this.selectedOption.setValue(null);
      return;
    }

    if (accessOptionsConfig[AccessValue.CandidateOnly].isEnabled) {
      this.selectedOption.setValue(AccessValue.CandidateOnly);
    } else if (
      !accessOptionsConfig[AccessValue.CandidateOnly].isEnabled &&
      accessOptionsConfig![AccessValue.AllCandidatesInOffer].isEnabled
    ) {
      this.selectedOption.setValue(AccessValue.AllCandidatesInOffer);
    } else if (
      !accessOptionsConfig![AccessValue.AllCandidatesInOffer].isEnabled &&
      accessOptionsConfig[AccessValue.DontRequest].isVisible
    ) {
      this.selectedOption.setValue(AccessValue.DontRequest);
    } else {
      this.selectedOption.setValue(null);
    }
  }
}
